import { useEffect, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Download, Loader2, Share2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "../site/Reveal";
import { API } from "@/lib/api";
import { getPublicConfig, trackEvent } from "@/lib/track";

const PROPERTY_TYPES = [
  "Boutique Hotel",
  "Luxury Resort",
  "Retreat / Wellness Property",
  "High-End Short-Term Rental / Airbnb",
  "Other Hospitality",
];

const BUSINESS_MODELS = [
  {
    value: "Hospitality & Accommodations",
    hint: "Boutique Hotel, Luxury Resort, Airbnb / Short-Term Rental, Retreat Venue",
  },
  {
    value: "Service-Based Business",
    hint: "Appointments, Local Trades, Wellness, Chiropractic, Specialty Food/Bakery",
  },
  {
    value: "Experience & Community Business",
    hint: "Digital Memberships, Community Vaults, Live Event/Retreat Hosting",
  },
];

const QUESTIONS = [
  {
    q: "How many hours a week do you or your team spend managing guest reviews and your online reputation?",
    options: [
      { label: "Under 2 hours — handled with ease", points: 3 },
      { label: "2–5 hours", points: 2 },
      { label: "5–10 hours", points: 1 },
      { label: "10+ hours — it consumes the week", points: 0 },
    ],
  },
  {
    q: "How quickly do you respond to a new guest review?",
    options: [
      { label: "Within 24 hours, every time", points: 3 },
      { label: "Within a few days", points: 2 },
      { label: "When we remember to", points: 1 },
      { label: "Rarely or never", points: 0 },
    ],
  },
  {
    q: "How would you describe your property's documented service standards (SOPs)?",
    options: [
      { label: "Fully documented and followed by the whole team", points: 3 },
      { label: "Documented, but inconsistently followed", points: 2 },
      { label: "Informal — they live in someone's head", points: 1 },
      { label: "We don't have any", points: 0 },
    ],
  },
  {
    q: "How much of your own week is spent on tasks that could be systematized or delegated?",
    options: [
      { label: "Less than 10%", points: 3 },
      { label: "10–25%", points: 2 },
      { label: "25–50%", points: 1 },
      { label: "More than half", points: 0 },
    ],
  },
  {
    q: "When occupancy or bookings dip, how quickly can you pinpoint why?",
    options: [
      { label: "Within days — we track the signals", points: 3 },
      { label: "Within a few weeks", points: 2 },
      { label: "We notice after the quarter", points: 1 },
      { label: "We usually guess", points: 0 },
    ],
  },
  {
    q: "How does your review volume and rating compare with your closest competitors?",
    options: [
      { label: "Clearly ahead", points: 3 },
      { label: "Roughly on par", points: 2 },
      { label: "Noticeably behind", points: 1 },
      { label: "Far behind / not sure", points: 0 },
    ],
  },
  {
    q: "If you stepped away for two weeks, what would happen to the guest experience?",
    options: [
      { label: "Nothing — the systems run it", points: 3 },
      { label: "Minor wobbles", points: 2 },
      { label: "A noticeable decline", points: 1 },
      { label: "It would fall apart", points: 0 },
    ],
  },
];

const STEP_LABELS = ["Your Details", "The Audit", "Your Results"];

const emptyLead = { name: "", business_name: "", email: "", phone: "", property_type: "", business_model: "", website: "" };

export default function AuditFlow() {
  const [step, setStep] = useState(1);
  const [lead, setLead] = useState(emptyLead);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [calendarUrl, setCalendarUrl] = useState("");
  const [noteVideoUrl, setNoteVideoUrl] = useState("");

  useEffect(() => {
    getPublicConfig().then((c) => {
      setCalendarUrl(c.scheduling_link || "");
      setNoteVideoUrl(c.note_video_url || "");
    });
  }, []);

  const update = (k) => (e) =>
    setLead((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#241E16", scale: 2 });
      const a = document.createElement("a");
      a.download = `time-wealth-audit-${(result?.band || "results").toLowerCase().replace(/\s+/g, "-")}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success("Results card downloaded.");
    } catch {
      toast.error("Couldn't render the card. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const shareResults = async () => {
    if (!result) return;
    const url = `${window.location.origin}/hospitality-advisory`;
    const text = `My Time-Wealth Audit: ${result.score}/21 — ${result.band}. Take the FREE audit:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Time-Wealth Audit Results", text, url });
      } catch { /* share cancelled — no-op */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Results copied — paste anywhere to share.");
    } catch {
      toast.error("Copy failed — download your results card instead.");
    }
  };

  const startAudit = (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim() || !lead.business_name.trim()) {
      toast.error("Please share your name, property name, and email.");
      return;
    }
    if (!lead.business_model) {
      toast.error("Please select your primary business model.");
      return;
    }
    trackEvent("audit_started", "advisory");
    axios
      .post(`${API}/audit/start`, {
        name: lead.name, email: lead.email,
        business_name: lead.business_name, website: lead.website,
      })
      .catch(() => {}); // nudge tracking must never block the audit
    setStep(2);
  };

  const submitAudit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/audit`, { ...lead, answers: finalAnswers });
      setResult(data);
      setStep(3);
    } catch (err) {
      toast.error("Something went wrong scoring your audit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const answer = (points) => {
    const next = [...answers, points];
    setAnswers(next);
    setTimeout(() => {
      if (next.length < QUESTIONS.length) {
        setQIndex(next.length);
      } else {
        submitAudit(next);
      }
    }, 240);
  };

  const back = () => {
    if (qIndex === 0) {
      setStep(1);
      return;
    }
    setAnswers((a) => a.slice(0, -1));
    setQIndex((i) => i - 1);
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-base text-[var(--cream)] placeholder:text-[var(--cream)]/35 focus:outline-none focus:border-[var(--gold)] transition-colors duration-300";
  const selectCls =
    "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-base text-[var(--cream)] focus:ring-0 focus:border-[var(--gold)]";

  return (
    <section id="audit" data-testid="audit-flow" className="relative bg-[var(--charcoal)] text-[var(--cream)] py-24 lg:py-32 grain overflow-hidden">
      <div className="beam-glow pointer-events-none absolute inset-0 opacity-50" />
      <div className="container-luxe relative z-10">
        <Reveal>
          <p className="overline flex items-center gap-3 text-[var(--gold-soft)]">
            <span className="h-px w-8 bg-[var(--gold)]" />
            FREE · The Time-Wealth Audit
          </p>
          <h2 className="mt-6 max-w-2xl serif text-[clamp(1.9rem,4vw,3.2rem)] leading-[1.06] tracking-tight">
            Where are your hours{" "}
            <span className="italic text-[var(--gold-soft)]">really going?</span>
          </h2>
        </Reveal>

        {/* Step indicator */}
        <div className="mt-12 flex items-center gap-3 sm:gap-5" data-testid="audit-steps-indicator">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-3 sm:gap-5">
              <div className="flex items-center gap-2.5">
                <span
                  className={`inline-flex h-8 w-8 items-center justify-center border text-xs transition-colors duration-400 ${
                    step > i + 1
                      ? "bg-[var(--gold)] border-[var(--gold)] text-[var(--charcoal)]"
                      : step === i + 1
                        ? "border-[var(--gold)] text-[var(--gold-soft)]"
                        : "border-white/15 text-[var(--cream)]/40"
                  }`}
                >
                  {i + 1}
                </span>
                <span className={`hidden sm:block text-xs uppercase tracking-[0.18em] ${step >= i + 1 ? "text-[var(--cream)]/85" : "text-[var(--cream)]/35"}`}>
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && <span className="h-px w-8 sm:w-14 bg-white/15" />}
            </div>
          ))}
        </div>

        <div className="mt-10 border border-white/12 bg-white/[0.04] backdrop-blur-sm p-7 sm:p-10 lg:p-14">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form
                key="step1"
                data-testid="audit-step1-form"
                onSubmit={startAudit}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="serif text-2xl tracking-tight">First, a proper introduction.</p>
                <p className="mt-2 text-sm text-[var(--cream)]/60">
                  Your results are prepared personally — tell us where to send them.
                </p>
                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>Website
                    <input type="text" tabIndex={-1} autoComplete="off" value={lead.website} onChange={update("website")} data-testid="audit-hp-website" />
                  </label>
                </div>
                <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  <div>
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Full Name</label>
                    <input data-testid="audit-input-name" value={lead.name} onChange={update("name")} placeholder="Your full name" className={inputCls} />
                  </div>
                  <div>
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Property Name</label>
                    <input data-testid="audit-input-business" value={lead.business_name} onChange={update("business_name")} placeholder="Your property or brand" className={inputCls} />
                  </div>
                  <div>
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Email</label>
                    <input data-testid="audit-input-email" type="email" value={lead.email} onChange={update("email")} placeholder="you@property.com" className={inputCls} />
                  </div>
                  <div>
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Phone <span className="normal-case tracking-normal">(optional)</span></label>
                    <input data-testid="audit-input-phone" value={lead.phone} onChange={update("phone")} placeholder="(000) 000-0000" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Property Type</label>
                    <Select value={lead.property_type} onValueChange={update("property_type")}>
                      <SelectTrigger data-testid="audit-select-property" className={selectCls}>
                        <SelectValue placeholder="Select your property type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`property-option-${t}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline block mb-2 !text-[var(--cream)]/50">Primary Business Model</label>
                    <Select value={lead.business_model} onValueChange={update("business_model")}>
                      <SelectTrigger data-testid="audit-select-business-model" className={selectCls}>
                        <SelectValue placeholder="What best describes your primary business model?" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_MODELS.map((m) => (
                          <SelectItem key={m.value} value={m.value} data-testid={`business-model-option-${m.value}`}>
                            <span className="block">{m.value}</span>
                            <span className="block text-xs text-muted-foreground">{m.hint}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="audit-continue-btn"
                  className="group mt-10 inline-flex items-center gap-3 bg-[var(--gold)] px-8 py-4 text-sm text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors duration-400"
                >
                  Begin the Seven Questions
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </motion.form>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                data-testid="audit-step2"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="overline !text-[var(--gold-soft)]" data-testid="audit-progress-label">
                    Question {Math.min(qIndex + 1, QUESTIONS.length)} of {QUESTIONS.length}
                  </p>
                  <button
                    type="button"
                    onClick={back}
                    data-testid="audit-back-btn"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--cream)]/50 hover:text-[var(--gold-soft)] transition-colors"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                </div>
                <div className="mt-4 h-px bg-white/12 relative overflow-hidden">
                  <motion.div
                    data-testid="audit-progress-bar"
                    className="absolute inset-y-0 left-0 bg-[var(--gold)]"
                    animate={{ width: `${(answers.length / QUESTIONS.length) * 100}%` }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>

                {submitting ? (
                  <div className="py-20 flex flex-col items-center gap-4" data-testid="audit-submitting">
                    <Loader2 size={26} className="animate-spin text-[var(--gold-soft)]" />
                    <p className="text-sm text-[var(--cream)]/60">Scoring your Time-Wealth Audit…</p>
                  </div>
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={qIndex}
                      initial={{ opacity: 0, x: 32 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -32 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="mt-9 serif text-xl sm:text-2xl lg:text-[1.7rem] leading-snug tracking-tight" data-testid="audit-question">
                        {QUESTIONS[qIndex].q}
                      </p>
                      <div className="mt-8 grid sm:grid-cols-2 gap-3">
                        {QUESTIONS[qIndex].options.map((opt, i) => (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => answer(opt.points)}
                            data-testid={`audit-option-${qIndex}-${i}`}
                            className="group border border-white/12 bg-white/[0.03] px-6 py-5 text-left text-sm leading-relaxed text-[var(--cream)]/80 transition-[background-color,border-color,color] duration-300 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 hover:text-[var(--cream)]"
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}
              </motion.div>
            )}

            {step === 3 && result && (
              <motion.div
                key="step3"
                data-testid="audit-results"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14">
                  <div>
                    <p className="overline !text-[var(--gold-soft)]">Your Time-Wealth Score</p>
                    <p className="mt-6 serif leading-none tracking-tight" data-testid="audit-score">
                      <span className="text-[clamp(4rem,9vw,7rem)] text-[var(--gold-soft)]">{result.score}</span>
                      <span className="text-2xl text-[var(--cream)]/45"> / 21</span>
                    </p>
                    <p className="mt-4 inline-block border border-[var(--gold)] px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[var(--gold-soft)]" data-testid="audit-band">
                      {result.band}
                    </p>
                    <p className="mt-6 text-base leading-relaxed text-[var(--cream)]/75" data-testid="audit-recommendation">
                      {result.recommendation}
                    </p>
                    {result.pain_points?.length > 0 && (
                      <div className="mt-8" data-testid="audit-pain-points">
                        <p className="overline !text-[var(--gold-soft)]">Your Three Pressure Points</p>
                        <div className="mt-5 space-y-5">
                          {result.pain_points.map((p, i) => (
                            <div key={p.title} className="flex gap-4" data-testid={`pain-point-${i}`}>
                              <span className="serif text-lg leading-none text-[var(--gold-soft)] mt-0.5">0{i + 1}</span>
                              <div>
                                <p className="text-sm font-semibold text-[var(--cream)]">{p.title}</p>
                                <p className="mt-1 text-sm leading-relaxed text-[var(--cream)]/55">{p.body}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="mt-6 text-sm leading-relaxed text-[var(--cream)]/50">
                      The natural next step: a complimentary 30-minute Operations Audit Call,
                      where we walk through your score and map what a 90-day transformation
                      would return to your calendar.
                    </p>

                    {/* Shareable results card */}
                    <div
                      ref={cardRef}
                      data-testid="audit-share-card"
                      className="relative mt-10 overflow-hidden border border-[var(--gold)]/50 bg-[#241E16] p-8"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-[var(--gold)]" />
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-soft)]">
                        Beam Concierge · Time-Wealth Audit
                      </p>
                      <p className="mt-5 serif leading-none tracking-tight">
                        <span className="text-6xl text-[var(--gold-soft)]">{result.score}</span>
                        <span className="text-xl text-[var(--cream)]/45"> / 21</span>
                      </p>
                      <p className="mt-3 inline-block border border-[var(--gold)] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[var(--gold-soft)]">
                        {result.band}
                      </p>
                      {lead.business_name && (
                        <p className="mt-4 serif italic text-sm text-[var(--cream)]/70">{lead.business_name}</p>
                      )}
                      {result.pain_points?.length > 0 && (
                        <div className="mt-5 space-y-2 border-t border-white/10 pt-5">
                          {result.pain_points.map((p, i) => (
                            <p key={p.title} className="text-xs leading-relaxed text-[var(--cream)]/65">
                              <span className="text-[var(--gold-soft)]">0{i + 1}</span> · {p.title}
                            </p>
                          ))}
                        </div>
                      )}
                      <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[var(--cream)]/40">
                        beamconcierge.com/hospitality-advisory
                      </p>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={downloadCard}
                        disabled={downloading}
                        data-testid="audit-download-card"
                        className="inline-flex items-center gap-2 bg-[var(--gold)] px-6 py-3 text-sm text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors duration-300 disabled:opacity-60"
                      >
                        {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        Download results card
                      </button>
                      <button
                        type="button"
                        onClick={shareResults}
                        data-testid="audit-share-results"
                        className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-sm text-[var(--cream)] hover:border-[var(--gold)] hover:text-[var(--gold-soft)] transition-colors duration-300"
                      >
                        <Share2 size={15} />
                        Share my results
                      </button>
                    </div>
                  </div>

                  <div>
                    {noteVideoUrl ? (
                      <div data-testid="results-video-note" className="border border-white/12 overflow-hidden">
                        <video controls preload="metadata" src={noteVideoUrl} className="w-full" />
                      </div>
                    ) : (
                      <div
                        data-testid="results-personal-note"
                        className="border border-[var(--gold)]/40 bg-white/[0.04] p-6 flex gap-4 items-start"
                      >
                        <img
                          src="/founder.jpg"
                          alt="Xena Redd, Founder and CEO of Beam Concierge"
                          className="h-14 w-14 rounded-full object-cover border border-[var(--gold)]/50 shrink-0"
                        />
                        <div>
                          <p className="serif italic text-base leading-relaxed text-[var(--cream)]/85">
                            "I've lived what your score is showing — the weeks where the business
                            owns your calendar instead of the other way around. Thirty minutes
                            together, and you'll leave knowing your first move."
                          </p>
                          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]">
                            Xena Redd · Founder &amp; CEO
                          </p>
                        </div>
                      </div>
                    )}
                    <p className="mt-6 overline !text-[var(--gold-soft)]">Reserve Your Operations Audit Call</p>
                    <div className="mt-6 border border-white/12 bg-[var(--cream)] overflow-hidden" data-testid="audit-calendar">
                      {calendarUrl ? (
                        <iframe
                          title="Book your Operations Audit Call"
                          src={calendarUrl}
                          data-testid="audit-calendar-iframe"
                          width="100%"
                          height="800"
                          scrolling="no"
                          loading="eager"
                          referrerPolicy="strict-origin-when-cross-origin"
                          style={{ display: "block", width: "100%", minHeight: 800, border: 0 }}
                          onLoad={() => trackEvent("audit_call_viewed", "advisory")}
                        />
                      ) : (
                        <div className="p-10 text-center">
                          <p className="text-sm text-[var(--charcoal)]/70">Our calendar is resting — reserve your call directly:</p>
                          <a
                            href={calendarUrl || "#"}
                            data-testid="audit-calendar-fallback"
                            className="group mt-5 inline-flex items-center gap-2 bg-[var(--gold)] px-6 py-3 text-sm text-[var(--charcoal)]"
                          >
                            Book your Operations Audit Call
                            <ArrowUpRight size={15} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
